// src/pages/Jobspage/JobDetails.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { jobsService, driversService } from "../../services";
import "./JobDetails.css";

// Status badge helper
const getStatusClass = (status) => {
  const statusMap = {
    'Pending': 'job-status-pending',
    'Approved': 'job-status-approved',
    'Assigned': 'job-status-assigned',
    'Picked Up': 'job-status-pickedup',
    'In Transit': 'job-status-transit',
    'Delivered': 'job-status-delivered',
    'Exception': 'job-status-exception',
    'Returned': 'job-status-returned',
    'Cancelled': 'job-status-cancelled',
  };
  return statusMap[status] || 'job-status-pending';
};

// Priority badge helper
const getPriorityClass = (priority) => {
  const priorityMap = {
    'Priority order': 'job-priority-urgent',
    'High': 'job-priority-high',
    'Medium': 'job-priority-normal',
    'Low': 'job-priority-low',
  };
  return priorityMap[priority] || 'job-priority-normal';
};

export default function JobDetails() {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  // Job state
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [assigningDriver, setAssigningDriver] = useState(false);

  // Schedule fields for assignment
  const [pickupDate, setPickupDate] = useState('');
  const [pickupWindowStart, setPickupWindowStart] = useState('');
  const [pickupWindowEnd, setPickupWindowEnd] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryWindowStart, setDeliveryWindowStart] = useState('');
  const [deliveryWindowEnd, setDeliveryWindowEnd] = useState('');

  // Status update state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Location modal state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [locationType, setLocationType] = useState('');

  // Fetch job details
  const fetchJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobsService.getJobById(jobId);
      setJob(data);
    } catch (err) {
      console.error('Fetch job error:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Fetch drivers for assignment
  const fetchDrivers = async () => {
    try {
      const [available, all] = await Promise.all([
        driversService.getAvailableDrivers({ limit: 50 }),
        driversService.getDrivers({ limit: 100 }),
      ]);
      setAvailableDrivers(available.drivers || []);
      setAllDrivers(all.drivers || []);
    } catch (err) {
      console.error('Fetch drivers error:', err);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  // Filter drivers by search
  const filteredDrivers = (driverSearch ? allDrivers : availableDrivers).filter(driver => {
    if (!driverSearch) return true;
    const searchLower = driverSearch.toLowerCase();
    return (
      driver.firstName?.toLowerCase().includes(searchLower) ||
      driver.lastName?.toLowerCase().includes(searchLower) ||
      driver.email?.toLowerCase().includes(searchLower) ||
      driver.driverProfile?.vehicle?.toLowerCase().includes(searchLower)
    );
  });

  // Open assign modal
  const openAssignModal = () => {
    fetchDrivers();
    setShowAssignModal(true);
    setSelectedDriverId(null);
    setDriverSearch('');
    // Pre-fill with existing schedule if any
    if (job?.pickupDate) {
      setPickupDate(job.pickupDate.split('T')[0]);
    }
    if (job?.pickupWindow) {
      setPickupWindowStart(job.pickupWindow.start || '');
      setPickupWindowEnd(job.pickupWindow.end || '');
    }
    if (job?.deliveryDate) {
      setDeliveryDate(job.deliveryDate.split('T')[0]);
    }
    if (job?.deliveryWindow) {
      setDeliveryWindowStart(job.deliveryWindow.start || '');
      setDeliveryWindowEnd(job.deliveryWindow.end || '');
    }
  };

  // Handle assign driver
  const handleAssignDriver = async () => {
    if (!selectedDriverId) return;

    try {
      setAssigningDriver(true);

      const assignmentData = { driverId: selectedDriverId };

      if (pickupDate) {
        assignmentData.pickupDate = new Date(pickupDate).toISOString();
      }
      if (pickupWindowStart || pickupWindowEnd) {
        assignmentData.pickupWindow = {
          start: pickupWindowStart || null,
          end: pickupWindowEnd || null,
        };
      }
      if (deliveryDate) {
        assignmentData.deliveryDate = new Date(deliveryDate).toISOString();
      }
      if (deliveryWindowStart || deliveryWindowEnd) {
        assignmentData.deliveryWindow = {
          start: deliveryWindowStart || null,
          end: deliveryWindowEnd || null,
        };
      }

      await jobsService.assignDriver(jobId, assignmentData);
      setShowAssignModal(false);
      fetchJobDetails();
    } catch (err) {
      console.error('Assign driver error:', err);
      alert('Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  // Handle status update
  const handleUpdateStatus = async () => {
    if (!newStatus) return;

    try {
      setUpdatingStatus(true);
      await jobsService.updateStatus(jobId, newStatus, { notes: statusNotes });
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
      fetchJobDetails();
    } catch (err) {
      console.error('Update status error:', err);
      alert(err.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle approve job
  const handleApproveJob = async () => {
    try {
      await jobsService.approveJob(jobId);
      fetchJobDetails();
    } catch (err) {
      console.error('Approve job error:', err);
      alert(err.message || 'Failed to approve job');
    }
  };

  // Handle cancel job
  const handleCancelJob = async () => {
    const reason = window.prompt('Enter cancellation reason (optional):');
    if (reason === null) return; // User clicked cancel button

    try {
      await jobsService.cancelJob(jobId, reason);
      fetchJobDetails();
    } catch (err) {
      console.error('Cancel job error:', err);
      alert(err.message || 'Failed to cancel job');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format time window
  const formatTimeWindow = (window) => {
    if (!window || (!window.start && !window.end)) return null;
    const start = window.start || '??:??';
    const end = window.end || '??:??';
    return `${start} - ${end}`;
  };

  // Get next available statuses based on workflow
  // Flow: Pending → Approved → Assigned → Picked Up → In Transit → Delivered
  const getNextStatuses = (currentStatus) => {
    const flow = {
      'Pending': ['Approved', 'Cancelled'],
      'Approved': ['Cancelled'], // Assigned happens via driver assignment
      'Assigned': ['Picked Up', 'Cancelled'],
      'Picked Up': ['In Transit', 'Exception'],
      'In Transit': ['Delivered', 'Exception'],
      'Exception': ['In Transit', 'Returned'],
      'Delivered': ['Returned'],
      'Returned': [],
      'Cancelled': [],
    };
    return flow[currentStatus] || [];
  };

  // Check if job can be assigned (only Approved jobs)
  const canAssignDriver = () => {
    return job?.status === 'Approved';
  };

  // Check if job can be cancelled
  const canCancelJob = () => {
    return ['Pending', 'Approved', 'Assigned'].includes(job?.status);
  };

  // Check if location has valid coordinates
  const hasValidCoordinates = (location) => {
    if (!location) return false;
    // Check for coordinates object
    if (location.coordinates) {
      const coords = location.coordinates;
      // Handle GeoJSON format [lng, lat]
      if (Array.isArray(coords) && coords.length >= 2) {
        return coords[0] != null && coords[1] != null && !isNaN(coords[0]) && !isNaN(coords[1]);
      }
      // Handle {lat, lng} or {latitude, longitude} format
      if (coords.lat != null && coords.lng != null) {
        return !isNaN(coords.lat) && !isNaN(coords.lng);
      }
      if (coords.latitude != null && coords.longitude != null) {
        return !isNaN(coords.latitude) && !isNaN(coords.longitude);
      }
    }
    // Check for direct lat/lng on location
    if (location.latitude != null && location.longitude != null) {
      return !isNaN(location.latitude) && !isNaN(location.longitude);
    }
    if (location.lat != null && location.lng != null) {
      return !isNaN(location.lat) && !isNaN(location.lng);
    }
    return false;
  };

  // Get coordinates from location object
  const getCoordinates = (location) => {
    if (!location) return null;
    // Check for coordinates object
    if (location.coordinates) {
      const coords = location.coordinates;
      // Handle GeoJSON format [lng, lat]
      if (Array.isArray(coords) && coords.length >= 2) {
        return { lat: coords[1], lng: coords[0] };
      }
      // Handle {lat, lng} format
      if (coords.lat != null && coords.lng != null) {
        return { lat: coords.lat, lng: coords.lng };
      }
      if (coords.latitude != null && coords.longitude != null) {
        return { lat: coords.latitude, lng: coords.longitude };
      }
    }
    // Check for direct lat/lng on location
    if (location.latitude != null && location.longitude != null) {
      return { lat: location.latitude, lng: location.longitude };
    }
    if (location.lat != null && location.lng != null) {
      return { lat: location.lat, lng: location.lng };
    }
    return null;
  };

  // Open location modal
  const openLocationModal = (location, type) => {
    const coords = getCoordinates(location);
    if (coords) {
      setLocationData({
        ...coords,
        name: location.name,
        address: location.address,
      });
      setLocationType(type);
      setShowLocationModal(true);
    }
  };

  if (loading) {
    return (
      <div className="job-details-container">
        <div className="job-details-loading">
          <div className="job-details-spinner"></div>
          <p>Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-details-container">
        <div className="job-details-error">
          <p>{error || 'Job not found'}</p>
          <button onClick={() => navigate('/jobs')}>Back to Jobs</button>
        </div>
      </div>
    );
  }

  const nextStatuses = getNextStatuses(job.status);

  return (
    <div className="job-details-container">
      {/* Header */}
      <div className="job-details-header">
        <button className="mq-back-btn" onClick={() => navigate('/jobs')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back to Jobs
        </button>
        <div className="job-header-actions">
          {/* Approve button for Pending jobs */}
          {job.status === 'Pending' && (
            <button className="job-action-btn success" onClick={handleApproveJob}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Approve Job
            </button>
          )}

          {/* Assign Driver button only for Approved jobs */}
          {canAssignDriver() && !job.driver && (
            <button className="job-action-btn primary" onClick={openAssignModal}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Assign Driver
            </button>
          )}

          {/* Update Status button */}
          {nextStatuses.length > 0 && (
            <button className="job-action-btn secondary" onClick={() => setShowStatusModal(true)}>
              Update Status
            </button>
          )}

          {/* Cancel button for cancellable jobs */}
          {canCancelJob() && (
            <button className="job-action-btn danger" onClick={handleCancelJob}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Cancel Job
            </button>
          )}
        </div>
      </div>

      {/* Job ID and Status Card */}
      <div className="job-main-card">
        <div className="job-main-header">
          <div className="job-id-section">
            <h1 className="job-id">{job.jobId}</h1>
            <span className={`job-status-badge large ${getStatusClass(job.status)}`}>
              {job.status}
            </span>
            <span className={`job-priority-badge ${getPriorityClass(job.priority)}`}>
              {job.priority || 'Medium'}
            </span>
          </div>
          <div className="job-meta-info">
            <span>Created: {formatDate(job.createdAt)} {formatTime(job.createdAt)}</span>
            {job.deliveredAt && <span>Delivered: {formatDate(job.deliveredAt)} {formatTime(job.deliveredAt)}</span>}
          </div>
        </div>

        {/* Route Information */}
        <div className="job-route-section">
          <div className="job-route-point pickup">
            <div className="job-route-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="6" stroke="currentColor" strokeWidth="2"/>
                <circle cx="10" cy="10" r="2" fill="currentColor"/>
              </svg>
            </div>
            <div className="job-route-details">
              <span className="job-route-label">Pickup</span>
              <span className="job-route-name">{job.from?.name || 'N/A'}</span>
              <span className="job-route-address">{job.from?.address || 'No address'}</span>
              {job.from?.phone && (
                <div className="job-route-phone-container">
                  <a href={`tel:${job.from.phone}`} className="job-route-phone-link">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M12.833 10.1v1.5a1 1 0 01-1.09.97 9.9 9.9 0 01-4.32-1.54 9.75 9.75 0 01-3-3 9.9 9.9 0 01-1.54-4.34 1 1 0 01.97-1.09h1.5a1 1 0 011 .86c.063.48.18.95.35 1.4a1 1 0 01-.23 1.06l-.64.64a8 8 0 003 3l.64-.64a1 1 0 011.06-.23c.45.17.92.287 1.4.35a1 1 0 01.86 1.02z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {job.from.phone}
                  </a>
                </div>
              )}
              {(job.pickupDate || job.pickupWindow?.start) && (
                <div className="job-schedule-info">
                  {job.pickupDate && <span className="job-schedule-date">{formatDate(job.pickupDate)}</span>}
                  {formatTimeWindow(job.pickupWindow) && (
                    <span className="job-schedule-window">{formatTimeWindow(job.pickupWindow)}</span>
                  )}
                </div>
              )}
              {hasValidCoordinates(job.from) && (
                <button
                  className="job-view-location-btn"
                  onClick={() => openLocationModal(job.from, 'Pickup')}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1C4.5 1 2.5 3 2.5 5.5C2.5 9 7 13 7 13S11.5 9 11.5 5.5C11.5 3 9.5 1 7 1Z" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="7" cy="5.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  View Location
                </button>
              )}
            </div>
          </div>
          <div className="job-route-connector">
            <div className="job-route-line"></div>
            {job.distance && <span className="job-route-distance">{job.distance} km</span>}
          </div>
          <div className="job-route-point delivery">
            <div className="job-route-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2C6.13 2 3 5.13 3 9c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2"/>
                <circle cx="10" cy="9" r="2" fill="currentColor"/>
              </svg>
            </div>
            <div className="job-route-details">
              <span className="job-route-label">Delivery</span>
              <span className="job-route-name">{job.to?.name || 'N/A'}</span>
              <span className="job-route-address">{job.to?.address || 'No address'}</span>
              {job.to?.phone && (
                <div className="job-route-phone-container">
                  <a href={`tel:${job.to.phone}`} className="job-route-phone-link">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M12.833 10.1v1.5a1 1 0 01-1.09.97 9.9 9.9 0 01-4.32-1.54 9.75 9.75 0 01-3-3 9.9 9.9 0 01-1.54-4.34 1 1 0 01.97-1.09h1.5a1 1 0 011 .86c.063.48.18.95.35 1.4a1 1 0 01-.23 1.06l-.64.64a8 8 0 003 3l.64-.64a1 1 0 011.06-.23c.45.17.92.287 1.4.35a1 1 0 01.86 1.02z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {job.to.phone}
                  </a>
                </div>
              )}
              {(job.deliveryDate || job.deliveryWindow?.start) && (
                <div className="job-schedule-info">
                  {job.deliveryDate && <span className="job-schedule-date">{formatDate(job.deliveryDate)}</span>}
                  {formatTimeWindow(job.deliveryWindow) && (
                    <span className="job-schedule-window">{formatTimeWindow(job.deliveryWindow)}</span>
                  )}
                </div>
              )}
              {hasValidCoordinates(job.to) && (
                <button
                  className="job-view-location-btn"
                  onClick={() => openLocationModal(job.to, 'Delivery')}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1C4.5 1 2.5 3 2.5 5.5C2.5 9 7 13 7 13S11.5 9 11.5 5.5C11.5 3 9.5 1 7 1Z" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="7" cy="5.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  View Location
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="job-details-grid">
        {/* Driver Section */}
        <div className="job-detail-card">
          <h3 className="job-card-title">Assigned Driver</h3>
          {job.driver ? (
            <div className="job-driver-info">
              <div className="job-driver-avatar">
                {job.driver.avatar ? (
                  <img src={job.driver.avatar} alt={job.driver.firstName} />
                ) : (
                  job.driver.firstName?.[0] || 'D'
                )}
              </div>
              <div className="job-driver-details">
                <span className="job-driver-name">{job.driver.firstName} {job.driver.lastName}</span>
                <span className="job-driver-meta">{job.driver.phone || job.driver.email}</span>
                <span className="job-driver-vehicle">{job.driver.driverProfile?.vehicle || 'No vehicle'}</span>
              </div>
              {job.status === 'Assigned' && (
                <button className="job-reassign-btn" onClick={openAssignModal}>
                  Reassign
                </button>
              )}
            </div>
          ) : (
            <div className="job-no-driver">
              <p>No driver assigned</p>
              {canAssignDriver() ? (
                <button className="job-assign-btn" onClick={openAssignModal}>
                  Assign Driver
                </button>
              ) : job.status === 'Pending' ? (
                <span className="job-status-hint">Approve job first to assign driver</span>
              ) : null}
            </div>
          )}
          {job.assignedAt && (
            <div className="job-assigned-info">
              Assigned on {formatDate(job.assignedAt)} {formatTime(job.assignedAt)}
            </div>
          )}
        </div>

        {/* Client Section */}
        <div className="job-detail-card">
          <h3 className="job-card-title">Client Information</h3>
          <div className="job-client-info">
            <div className="job-info-row">
              <label>Name</label>
              <span>{job.client?.firstName} {job.client?.lastName || ''}</span>
            </div>
            <div className="job-info-row">
              <label>Email</label>
              <span>{job.client?.email || 'N/A'}</span>
            </div>
            <div className="job-info-row">
              <label>Phone</label>
              <span>{job.client?.phone || 'N/A'}</span>
            </div>
            <div className="job-info-row">
              <label>Company</label>
              <span>{job.company?.name || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Job Details */}
        <div className="job-detail-card">
          <h3 className="job-card-title">Job Details</h3>
          <div className="job-info-list">
            <div className="job-info-row">
              <label>QR Code</label>
              <span className="job-qr-code">{job.qrCode}</span>
            </div>
            <div className="job-info-row">
              <label>Scheduled Date</label>
              <span>{formatDate(job.scheduledDate)}</span>
            </div>
            {job.estimatedDuration && (
              <div className="job-info-row">
                <label>Est. Duration</label>
                <span>{job.estimatedDuration} min</span>
              </div>
            )}
            {job.notes && (
              <div className="job-info-row full-width">
                <label>Notes</label>
                <span>{job.notes}</span>
              </div>
            )}
          </div>
        </div>

        {/* Proof of Delivery */}
        {job.proofOfDelivery && (job.proofOfDelivery.signature || job.proofOfDelivery.photos?.length > 0) && (
          <div className="job-detail-card">
            <h3 className="job-card-title">Proof of Delivery</h3>
            <div className="job-pod-info">
              {job.proofOfDelivery.recipientName && (
                <div className="job-info-row">
                  <label>Recipient</label>
                  <span>{job.proofOfDelivery.recipientName}</span>
                </div>
              )}
              {job.proofOfDelivery.timestamp && (
                <div className="job-info-row">
                  <label>Signed At</label>
                  <span>{formatDate(job.proofOfDelivery.timestamp)} {formatTime(job.proofOfDelivery.timestamp)}</span>
                </div>
              )}
              {job.proofOfDelivery.signature && (
                <div className="job-signature">
                  <label>Signature</label>
                  <img src={job.proofOfDelivery.signature} alt="Signature" />
                </div>
              )}
              {job.proofOfDelivery.photos?.length > 0 && (
                <div className="job-pod-photos">
                  <label>Photos</label>
                  <div className="job-photos-grid">
                    {job.proofOfDelivery.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Proof of delivery ${idx + 1}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status Timeline */}
      <div className="job-timeline-card">
        <h3 className="job-card-title">Status History</h3>
        <div className="job-timeline">
          {job.statusHistory && job.statusHistory.length > 0 ? (
            job.statusHistory.slice().reverse().map((history, index) => (
              <div key={index} className={`job-timeline-item ${index === 0 ? 'current' : ''}`}>
                <div className={`job-timeline-dot ${getStatusClass(history.status)}`}></div>
                <div className="job-timeline-content">
                  <div className="job-timeline-header">
                    <span className={`job-status-badge small ${getStatusClass(history.status)}`}>
                      {history.status}
                    </span>
                    <span className="job-timeline-date">
                      {formatDate(history.timestamp)} {formatTime(history.timestamp)}
                    </span>
                  </div>
                  {history.notes && <p className="job-timeline-notes">{history.notes}</p>}
                  {history.updatedBy && (
                    <span className="job-timeline-user">
                      By: {history.updatedBy.firstName || 'System'}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="job-timeline-empty">No status history available</div>
          )}
        </div>
      </div>

      {/* Exception Info */}
      {job.status === 'Exception' && (job.exceptionReason || job.exceptionNotes) && (
        <div className="job-exception-card">
          <h3 className="job-card-title">Exception Details</h3>
          {job.exceptionReason && (
            <div className="job-exception-reason">
              <label>Reason</label>
              <span>{job.exceptionReason}</span>
            </div>
          )}
          {job.exceptionNotes && (
            <div className="job-exception-notes">
              <label>Notes</label>
              <span>{job.exceptionNotes}</span>
            </div>
          )}
        </div>
      )}

      {/* Assign Driver Modal */}
      {showAssignModal && (
        <div className="job-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="job-modal job-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="job-modal-header">
              <h3>Assign Driver to {job.jobId}</h3>
              <button className="job-modal-close" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="job-modal-body">
              {/* Driver Search */}
              <div className="job-driver-search">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                  <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search drivers by name, email, or vehicle..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                />
                {driverSearch && (
                  <button className="job-search-clear" onClick={() => setDriverSearch('')}>×</button>
                )}
              </div>

              {/* Drivers List */}
              <div className="job-assign-section">
                <h4 className="job-section-title">
                  {driverSearch ? 'Search Results' : 'Available Drivers'}
                  <span className="job-driver-count">({filteredDrivers.length})</span>
                </h4>
                {filteredDrivers.length === 0 ? (
                  <div className="job-empty">
                    <p>{driverSearch ? 'No drivers match your search' : 'No available drivers'}</p>
                  </div>
                ) : (
                  <div className="job-drivers-list">
                    {filteredDrivers.map((driver) => (
                      <div
                        key={driver._id}
                        className={`job-driver-item ${selectedDriverId === driver._id ? 'selected' : ''}`}
                        onClick={() => setSelectedDriverId(driver._id)}
                      >
                        <div className="job-driver-info">
                          <div className="job-driver-avatar">
                            {driver.firstName?.[0] || 'D'}
                          </div>
                          <div>
                            <div className="job-driver-name">
                              {driver.firstName} {driver.lastName}
                            </div>
                            <div className="job-driver-meta">
                              {driver.driverProfile?.vehicle || 'No vehicle'} • ★ {driver.rating?.toFixed(1) || '0.0'}
                            </div>
                            <div className={`job-driver-status ${driver.driverProfile?.status === 'Online' ? 'online' : 'offline'}`}>
                              {driver.driverProfile?.status || 'Offline'}
                            </div>
                          </div>
                        </div>
                        {selectedDriverId === driver._id && (
                          <span className="job-driver-check">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Schedule Section */}
              {selectedDriverId && (
                <div className="job-assign-section">
                  <h4 className="job-section-title">Schedule (Optional)</h4>

                  <div className="job-schedule-group">
                    <label className="job-schedule-label">Pickup Schedule</label>
                    <div className="job-schedule-row">
                      <div className="job-schedule-field">
                        <label>Date</label>
                        <input
                          type="date"
                          value={pickupDate}
                          onChange={(e) => setPickupDate(e.target.value)}
                        />
                      </div>
                      <div className="job-schedule-field">
                        <label>From</label>
                        <input
                          type="time"
                          value={pickupWindowStart}
                          onChange={(e) => setPickupWindowStart(e.target.value)}
                        />
                      </div>
                      <div className="job-schedule-field">
                        <label>To</label>
                        <input
                          type="time"
                          value={pickupWindowEnd}
                          onChange={(e) => setPickupWindowEnd(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="job-schedule-group">
                    <label className="job-schedule-label">Delivery Schedule</label>
                    <div className="job-schedule-row">
                      <div className="job-schedule-field">
                        <label>Date</label>
                        <input
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                        />
                      </div>
                      <div className="job-schedule-field">
                        <label>From</label>
                        <input
                          type="time"
                          value={deliveryWindowStart}
                          onChange={(e) => setDeliveryWindowStart(e.target.value)}
                        />
                      </div>
                      <div className="job-schedule-field">
                        <label>To</label>
                        <input
                          type="time"
                          value={deliveryWindowEnd}
                          onChange={(e) => setDeliveryWindowEnd(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="job-modal-footer">
              <button className="job-btn-outline" onClick={() => setShowAssignModal(false)}>
                Cancel
              </button>
              <button
                className="job-btn-primary"
                onClick={handleAssignDriver}
                disabled={!selectedDriverId || assigningDriver}
              >
                {assigningDriver ? 'Assigning...' : 'Assign Driver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="job-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-modal-header">
              <h3>Update Job Status</h3>
              <button className="job-modal-close" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            <div className="job-modal-body">
              <div className="job-status-options">
                {nextStatuses.map((status) => (
                  <button
                    key={status}
                    className={`job-status-option ${newStatus === status ? 'selected' : ''} ${getStatusClass(status)}`}
                    onClick={() => setNewStatus(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
              <div className="job-form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add any notes about this status change..."
                  rows="3"
                />
              </div>
            </div>
            <div className="job-modal-footer">
              <button className="job-btn-outline" onClick={() => setShowStatusModal(false)}>
                Cancel
              </button>
              <button
                className="job-btn-primary"
                onClick={handleUpdateStatus}
                disabled={!newStatus || updatingStatus}
              >
                {updatingStatus ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && locationData && (
        <div className="job-modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="job-modal job-location-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-modal-header">
              <h3>{locationType} Location</h3>
              <button className="job-modal-close" onClick={() => setShowLocationModal(false)}>×</button>
            </div>
            <div className="job-modal-body">
              <div className="job-location-info">
                {locationData.name && (
                  <div className="job-location-name-lg">{locationData.name}</div>
                )}
                {locationData.address && (
                  <div className="job-location-address-lg">{locationData.address}</div>
                )}
                <div className="job-location-coords">
                  <div className="job-coord-item">
                    <label>Latitude</label>
                    <span>{locationData.lat?.toFixed(6)}</span>
                  </div>
                  <div className="job-coord-item">
                    <label>Longitude</label>
                    <span>{locationData.lng?.toFixed(6)}</span>
                  </div>
                </div>
              </div>
              <div className="job-map-container">
                <iframe
                  title={`${locationType} Location Map`}
                  width="100%"
                  height="300"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '8px' }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${locationData.lng - 0.01},${locationData.lat - 0.01},${locationData.lng + 0.01},${locationData.lat + 0.01}&layer=mapnik&marker=${locationData.lat},${locationData.lng}`}
                  allowFullScreen
                />
              </div>
              <div className="job-map-actions">
                <a
                  href={`https://www.google.com/maps?q=${locationData.lat},${locationData.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="job-btn-outline"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M10.5 3.5H5.5M10.5 3.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Open in Google Maps
                </a>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${locationData.lat},${locationData.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="job-btn-primary"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
